import React from "react";

import styles from "./LocationForm.module.css";
import {Button, ButtonState} from "../../../../elements/button/Button";
import {RootState} from "../../../../../app/store";
import {ValidatedForm} from "../../../../elements/validator/validatedForm/ValidatedForm";
import {
    ConfigurationRegion,
    CreateWorkingHoursRequest,
    EnquiryProvider,
    UpdateWorkingHoursRequest,
    VehicleDataProvider
} from "../../../../../api/generated";
import {CreateLocationRequestUI} from "../../locationCreateSlice";
import {UpdateLocationRequestUI} from "../../locationUpdateSlice";
import {VehicleTypesByManufacturer} from "../../manufacturerVehicleTypesSlice";
import {FormHeader} from "../../../../elements/formHeader/FormHeader";
import {LocationInformation} from "./components/LocationInformation";
import {WorkSchedule} from "./components/workSchedule/WorkSchedule";
import {VehicleInformation} from "./components/vehicleInformation/VehicleInformation";
import {EnquiryInformation} from "./components/enquiryInformation/EnquiryInformation";
import {AftersalesInformation} from "./components/aftersalesConfiguration/AftersalesInformation";
import {LocationEmailInformation} from "./components/locationEmailInformation/LocationEmailInformation";

export type LocationRequest = CreateLocationRequestUI | UpdateLocationRequestUI

export type WorkingHoursRequest = CreateWorkingHoursRequest | UpdateWorkingHoursRequest

export type LocationRequestPartial = Partial<LocationRequest>

export type LocationFormSelectionProvider<W> = ({
    publicId: (state: RootState) => string | undefined;
    name: (state: RootState) => string | undefined;
    city: (state: RootState) => string | undefined;
    postcode: (state: RootState) => string | undefined;
    phoneNumber: (state: RootState) => string | undefined;

    mondayWorkingHoursRequest: (state: RootState) => W | undefined;
    tuesdayWorkingHoursRequest: (state: RootState) => W | undefined;
    wednesdayWorkingHoursRequest: (state: RootState) => W | undefined;
    thursdayWorkingHoursRequest: (state: RootState) => W | undefined;
    fridayWorkingHoursRequest: (state: RootState) => W | undefined;
    saturdayWorkingHoursRequest: (state: RootState) => W | undefined;
    sundayWorkingHoursRequest: (state: RootState) => W | undefined;

    vehicleDataProvider: (state: RootState) => VehicleDataProvider | undefined;
    advertiserId: (state: RootState) => string | undefined;
    filePath: (state: RootState) => string | undefined;
    feedId: (state: RootState) => string | undefined;
    locationHash: (state: RootState) => string | undefined;

    locationEmailTranscriptRecipients: (state: RootState) => string | undefined;
    locationEmailFiltrationEnabled: (state: RootState) => boolean | undefined;
    locationEmailTranscriptSummaryEnabled: (state: RootState) => boolean | undefined;

    enquiryProvider: (state: RootState) => EnquiryProvider | undefined;
    dragon2000DealerId: (state: RootState) => string | undefined;
    em2Region: (state: RootState) => ConfigurationRegion | undefined;

    aftersalesEnabled: (state: RootState) => boolean | undefined;
    serviceBookingUrl: (state: RootState) => string | undefined;
    recallUrl: (state: RootState) => string | undefined;
    aftersalesEmailTranscriptRecipients: (state: RootState) => string | undefined;
    aftersalesEmailFiltrationEnabled: (state: RootState) => boolean | undefined;
    aftersalesEmailTranscriptSummaryEnabled: (state: RootState) => boolean | undefined;
    supportedVehicleManufacturers: (state: RootState) => VehicleTypesByManufacturer | undefined;

    mondayServiceWorkingHoursRequest: (state: RootState) => W | undefined;
    tuesdayServiceWorkingHoursRequest: (state: RootState) => W | undefined;
    wednesdayServiceWorkingHoursRequest: (state: RootState) => W | undefined;
    thursdayServiceWorkingHoursRequest: (state: RootState) => W | undefined;
    fridayServiceWorkingHoursRequest: (state: RootState) => W | undefined;
    saturdayServiceWorkingHoursRequest: (state: RootState) => W | undefined;
    sundayServiceWorkingHoursRequest: (state: RootState) => W | undefined;
});

interface Props<W> {
    formTitle: string
    onClose: () => any;
    onSubmit: () => any;
    updateRequest: (update: LocationRequestPartial) => void;
    selectionProvider: LocationFormSelectionProvider<W>;
    updateWorkingHours: (update: WorkingHoursRequest & { fieldName: string }) => void;
    updateAftersalesWorkingHours: (update: WorkingHoursRequest & { fieldName: string }) => void;
}

export const LocationForm = <
    T extends LocationRequest,
    W extends WorkingHoursRequest
>({
      formTitle,
      onClose,
      onSubmit,
      updateRequest,
      selectionProvider,
      updateWorkingHours,
      updateAftersalesWorkingHours
  }: Props<W>) => {

    return <ValidatedForm<T> submitForm={onSubmit}>
        {(formCtx) => {
            const {formDisabled, onSubmitClick, updateValidationStatus, getMessage} = formCtx;
            return <div className={styles.wrapper}>
                <FormHeader
                    formTitle={formTitle}
                    onClose={onClose}
                />
                <div className={styles.fieldsWrapper}>
                    <LocationInformation<T, W>
                        getMessage={getMessage}
                        updateRequest={updateRequest}
                        selectionProvider={selectionProvider}
                        updateValidationStatus={updateValidationStatus}
                    />
                    <VehicleInformation<T, W>
                        getMessage={getMessage}
                        updateRequest={updateRequest}
                        selectionProvider={selectionProvider}
                        updateValidationStatus={updateValidationStatus}
                    />
                    <EnquiryInformation<T, W>
                        getMessage={getMessage}
                        updateRequest={updateRequest}
                        selectionProvider={selectionProvider}
                        updateValidationStatus={updateValidationStatus}
                    />
                    <LocationEmailInformation<T, W>
                        getMessage={getMessage}
                        updateRequest={updateRequest}
                        selectionProvider={selectionProvider}
                        updateValidationStatus={updateValidationStatus}
                    />
                    <WorkSchedule<W>
                        title={"Location Availability"}
                        updateWorkingHours={updateWorkingHours}
                        mondayWorkingHoursSelector={selectionProvider.mondayWorkingHoursRequest}
                        tuesdayWorkingHoursSelector={selectionProvider.tuesdayWorkingHoursRequest}
                        wednesdayWorkingHoursSelector={selectionProvider.wednesdayWorkingHoursRequest}
                        thursdayWorkingHoursSelector={selectionProvider.thursdayWorkingHoursRequest}
                        fridayWorkingHoursSelector={selectionProvider.fridayWorkingHoursRequest}
                        saturdayWorkingHoursSelector={selectionProvider.saturdayWorkingHoursRequest}
                        sundayWorkingHoursSelector={selectionProvider.sundayWorkingHoursRequest}
                    />
                    <AftersalesInformation<T, W>
                        getMessage={getMessage}
                        updateRequest={updateRequest}
                        selectionProvider={selectionProvider}
                        updateWorkingHours={updateAftersalesWorkingHours}
                        updateValidationStatus={updateValidationStatus}
                    />
                </div>
                <div className={styles.buttonsWrapper}>
                    <Button
                        title={"Close"}
                        state={ButtonState.SECONDARY}
                        handleClick={() => onClose()}
                    />
                    <Button
                        title={formTitle}
                        state={formDisabled ? ButtonState.DISABLED : ButtonState.PRIMARY}
                        handleClick={() => onSubmitClick()}
                    />
                </div>
            </div>
        }}
    </ValidatedForm>
}
